import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import type { FinanceiroLojaFilters } from "hooks/useFinanceiroLoja";
import { FINANCEIRO_ACCENT, financeiroToolbarSx } from "./financeiro-lancamentos.theme";

const TIPO_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Todos" },
  { value: "RECEITA", label: "Receita" },
  { value: "DESPESA", label: "Despesa" },
];

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Todos" },
  { value: "PENDENTE", label: "Pendente" },
  { value: "CONFIRMADO", label: "Confirmado" },
  { value: "CANCELADO", label: "Cancelado" },
];

const ORIGEM_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Todas" },
  { value: "PEDIDO", label: "Pedido (legado)" },
  { value: "VENDA", label: "Venda" },
  { value: "FRETE", label: "Frete" },
  { value: "TAXA_GATEWAY", label: "Taxa gateway" },
  { value: "ESTORNO", label: "Estorno" },
  { value: "AJUSTE", label: "Ajuste" },
];

const PAGE_SIZE_OPTIONS = [10, 20, 25, 50, 100];

type Props = {
  pending: FinanceiroLojaFilters;
  pageSize: number;
  total: number;
  onChange: <K extends keyof FinanceiroLojaFilters>(
    field: K,
    value: FinanceiroLojaFilters[K]
  ) => void;
  onPageSizeChange: (size: number) => void;
  onApply: () => void;
  onClear: () => void;
};

export default function FinanceiroFilters({
  pending,
  pageSize,
  total,
  onChange,
  onPageSizeChange,
  onApply,
  onClear,
}: Props) {
  return (
    <MDBox sx={financeiroToolbarSx}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <ReceiptLongOutlinedIcon sx={{ color: FINANCEIRO_ACCENT }} />
          <MDTypography variant="button" fontWeight="medium" color="text">
            {total > 0
              ? `${total} lançamento${total === 1 ? "" : "s"} no período / filtros aplicados`
              : "Nenhum lançamento no filtro atual"}
          </MDTypography>
        </Stack>

        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel shrink htmlFor="fin-data-inicio">
                Data inicial
              </InputLabel>
              <OutlinedInput
                id="fin-data-inicio"
                type="date"
                value={pending.dataInicio}
                onChange={(e) => onChange("dataInicio", e.target.value)}
                label="Data inicial"
                notched
                sx={{ bgcolor: "background.paper", borderRadius: 1.5 }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel shrink htmlFor="fin-data-fim">
                Data final
              </InputLabel>
              <OutlinedInput
                id="fin-data-fim"
                type="date"
                value={pending.dataFim}
                onChange={(e) => onChange("dataFim", e.target.value)}
                label="Data final"
                notched
                sx={{ bgcolor: "background.paper", borderRadius: 1.5 }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel shrink htmlFor="fin-pedido-id">
                ID pedido
              </InputLabel>
              <OutlinedInput
                id="fin-pedido-id"
                value={pending.pedidoId}
                onChange={(e) => onChange("pedidoId", e.target.value)}
                label="ID pedido"
                placeholder="cuid do pedido"
                notched
                sx={{ bgcolor: "background.paper", borderRadius: 1.5 }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel shrink htmlFor="fin-pagamento-id">
                ID pagamento
              </InputLabel>
              <OutlinedInput
                id="fin-pagamento-id"
                value={pending.pagamentoId}
                onChange={(e) => onChange("pagamentoId", e.target.value)}
                label="ID pagamento"
                notched
                sx={{ bgcolor: "background.paper", borderRadius: 1.5 }}
              />
            </FormControl>
          </Grid>
        </Grid>

        <Stack spacing={0.75}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Origem do lançamento
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={pending.origem}
            onChange={(_, next: string | null) => {
              if (next === null) return;
              onChange("origem", next);
            }}
            sx={{
              flexWrap: "wrap",
              "& .MuiToggleButton-root": {
                px: 1.25,
                py: 0.75,
                textTransform: "none",
                fontSize: "0.8125rem",
                "&.Mui-selected": {
                  bgcolor: "rgba(249, 96, 48, 0.1)",
                  borderColor: FINANCEIRO_ACCENT,
                  color: FINANCEIRO_ACCENT,
                  fontWeight: 600,
                },
              },
            }}
          >
            {ORIGEM_OPTIONS.map((opt) => (
              <ToggleButton key={opt.value || "all-origem"} value={opt.value}>
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>

        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", lg: "center" }}
          justifyContent="space-between"
        >
          <ToggleButtonGroup
            exclusive
            size="small"
            value={pending.tipo}
            onChange={(_, next: string | null) => {
              if (next === null) return;
              onChange("tipo", next);
            }}
            sx={{
              flexWrap: "wrap",
              "& .MuiToggleButton-root": {
                px: 1.25,
                py: 0.75,
                textTransform: "none",
                fontSize: "0.8125rem",
                "&.Mui-selected": {
                  bgcolor: "rgba(249, 96, 48, 0.1)",
                  borderColor: FINANCEIRO_ACCENT,
                  color: FINANCEIRO_ACCENT,
                  fontWeight: 600,
                },
              },
            }}
          >
            {TIPO_OPTIONS.map((opt) => (
              <ToggleButton key={opt.value || "all-tipo"} value={opt.value}>
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          <ToggleButtonGroup
            exclusive
            size="small"
            value={pending.status}
            onChange={(_, next: string | null) => {
              if (next === null) return;
              onChange("status", next);
            }}
            sx={{
              flexWrap: "wrap",
              "& .MuiToggleButton-root": {
                px: 1.25,
                py: 0.75,
                textTransform: "none",
                fontSize: "0.8125rem",
                "&.Mui-selected": {
                  bgcolor: "rgba(249, 96, 48, 0.1)",
                  borderColor: FINANCEIRO_ACCENT,
                  color: FINANCEIRO_ACCENT,
                  fontWeight: 600,
                },
              },
            }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <ToggleButton key={opt.value || "all-status"} value={opt.value}>
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <MDButton
              variant="gradient"
              color="dark"
              size="small"
              onClick={onApply}
              sx={{
                background: `linear-gradient(195deg, ${FINANCEIRO_ACCENT}, #e04a1a)`,
              }}
            >
              Aplicar filtros
            </MDButton>
            <MDButton variant="outlined" color="dark" size="small" onClick={onClear}>
              Limpar
            </MDButton>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="caption" color="text.secondary">
              Por página
            </Typography>
            <Select
              size="small"
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              sx={{ minWidth: 72, bgcolor: "background.paper", borderRadius: 1.5 }}
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <MenuItem key={n} value={n}>
                  {n}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        </Stack>
      </Stack>
    </MDBox>
  );
}
